import { useCallback, useEffect, useRef, useState } from "react";
import { listClientDocuments, listFolders } from "@/features/documents/api";
import type { ClientDocument, DocumentFolder } from "@/features/documents/types";
import { ApiError } from "@/lib/api";

interface UseClientArchiveResult {
  folders: DocumentFolder[];
  documents: ClientDocument[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Loads a client's folder tree + all their documents together (the page filters
 * by the selected folder client-side). Same race guards as use-cases.ts.
 */
export function useClientArchive(clientId: string): UseClientArchiveResult {
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${clientId}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    Promise.all([listFolders(clientId), listClientDocuments(clientId)])
      .then(([f, d]) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setFolders(f);
        setDocuments(d);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setFolders([]);
        setDocuments([]);
        setError(err instanceof ApiError ? err.message : "Failed to load documents.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId, reloadKey]);

  return { folders, documents, loading, error, refetch };
}
